import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PolicyApi } from '@Apis';
import { OverAllPolicyType, ServiceCollection } from '../../utils/types';
import { createPolicyQueryKey } from './usePolicyListQuery';
import { updatePolicyStatusInCache } from '../utils/policyCacheUpdaters';
import {
  createPolicyMutationKey,
  PolicyMutationKey,
} from '../context/PolicyMutationContext';

interface UsePolicyStatusMutationParams {
  overAllPolicyType: OverAllPolicyType;
  collectionType: ServiceCollection;
  orgId: string;
}

interface StatusMutationVariables {
  policyId: string;
  currentStatus: number;
}

export const usePolicyStatusMutation = ({
  overAllPolicyType,
  collectionType,
  orgId,
}: UsePolicyStatusMutationParams) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = createPolicyQueryKey(
    overAllPolicyType,
    collectionType,
    orgId
  );

  const [mutatingPolicyTypes, setMutatingPolicyTypes] = useState<
    Set<PolicyMutationKey>
  >(new Set());

  const mutation = useMutation({
    mutationFn: ({ policyId, currentStatus }: StatusMutationVariables) =>
      PolicyApi.apiUpdatePolicyStatus(policyId, currentStatus),

    onMutate: async ({ policyId, currentStatus }: StatusMutationVariables) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      // Get the policy from the cache and create mutation key
      const cacheData = previousData as any;
      const policy = cacheData?.data?.find(
        (p: any) => p.policyUuid === policyId
      );
      const mutationKey: PolicyMutationKey | undefined =
        policy?.policyType !== undefined
          ? createPolicyMutationKey(
              policy.policyType,
              policy.policySubType,
              overAllPolicyType,
              collectionType,
              orgId
            )
          : undefined;

      if (mutationKey) {
        setMutatingPolicyTypes((prev) => new Set(prev).add(mutationKey));
      }

      const newStatus = currentStatus === 1 ? 0 : 1;

      queryClient.setQueryData(queryKey, (oldData: any) =>
        updatePolicyStatusInCache(oldData, policyId, newStatus)
      );

      return { previousData, mutationKey };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    },

    onSettled: (_data, _error, _variables, context) => {
      // Remove mutation key from mutating set
      const keyToRemove = context?.mutationKey;
      if (keyToRemove) {
        setMutatingPolicyTypes((prev) => {
          const next = new Set(prev);
          next.delete(keyToRemove);
          return next;
        });
      }

      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    ...mutation,
    mutatingPolicyTypes,
  };
};
